import { Axios, ensureFreshAuth } from "@/apis/axios";

/**
 * 제출 응답. platform의 create serializer와 같은 형태이며, 서버는 이보다 많은 필드를 준다
 * (`initial_type`, `is_sequential`, `sangjo_comparison_id` 등). 이 앱이 선언해 온 범위만 유지한다.
 */
export interface QuotationResponse {
  id: number;
  status: string;
  funeral_sido: string;
  funeral_sigungu: string;
  needs_binso: boolean;
  condolers: string;
}

export interface QuotationRequestBody {
  status: string;
  funeral_sido: string;
  funeral_sigungu: string;
  needs_binso: boolean;
  condolers: string;
}

// platform의 getSangjoQuotationStatus와 같은 변환을 적용한다.
const getQuotationStatus = (status: string) => {
  if (status === "1주에서 한 달 정도 기간이 남은 것 같아요.") {
    return "1달 정도 기간이 남은 것 같아요.";
  }
  if (status === "임종이 며칠 남지 않았어요.") {
    return "임종이 얼마 남지 않았어요.";
  }
  return status;
};

/**
 * 견적 요청 제출. 서버가 토큰 유무로 회원/비회원을 판정하므로 프론트는 분기하지 않는다.
 *
 * - 비회원: 항상 create (201, user=null orphan)
 * - 회원 + 기존 견적 없음: create (201)
 * - 회원 + 기존 견적 있음: in-place merge (200). 409 없음
 *
 * 응답 `id`는 Request id다. 로그인 handoff의 qid로 그대로 넘긴다.
 *
 * 제출 직전 `ensureFreshAuth`로 access를 되살린다. 이 앱에는 전역 로그인 상태가 없어
 * 반환값을 쓰지 않지만, 서버가 만료된 회원을 익명으로 처리해 orphan을 만드는 건 막아야 한다.
 */
export const submitSangjoQuotationRequest = async (
  body: Readonly<QuotationRequestBody>
) => {
  await ensureFreshAuth();

  return Axios.post<QuotationResponse>(`/sangjo_quotation_requests/v2/`, {
    ...body,
    status: getQuotationStatus(body.status),
  });
};

/**
 * 견적 생성 serializer는 memo를 저장하지 않으므로 생성 성공 후 별도로 기록한다.
 * memo 기록 실패가 리드 흐름을 막지 않도록 호출부에서 fail-open으로 처리한다.
 *
 * 제출용 v2와 달리 이 PATCH는 필드 수정용이라 v1을 그대로 쓴다. 버전 차이가 아니라 역할이 다르다.
 */
export const patchSangjoQuotationMemo = (id: number, memo: string) =>
  Axios.patch(`/sangjo_quotation_requests/${id}/`, {
    memo: memo.replace(/\|/g, " ").slice(0, 255),
  });
